"use client"

import { useState } from "react"
import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import { ExternalLink, Github, Code, Cpu, Globe, Smartphone, ArrowRight } from "lucide-react"
import Image from "next/image"
import CustomImage from "@/components/ui/customimage"
import { projects } from "@/components/Constants/projectData"
import Link from "next/link"

type ProjectType = "all" | "web" | "mobile" | "ai" | "design"



export default function ProjectSection() {
  const [activeFilter, setActiveFilter] = useState<ProjectType>("all")

  

  const filterTypes = [
    { value: "all", label: "All Projects", icon: Globe },
    { value: "web", label: "Web Apps", icon: Code },
    { value: "mobile", label: "Mobile Apps", icon: Smartphone },
    { value: "ai", label: "AI Solutions", icon: Cpu },
    { value: "design", label: "Design Work", icon: Globe },
  ]

  const filteredProjects =
    activeFilter === "all" ? projects : projects.filter((project) => project.type.includes(activeFilter))

  const getProjectTypeIcon = (type: ProjectType) => {
    switch (type) {
      case "web":
        return <Code className="h-4 w-4" />
      case "mobile":
        return <Smartphone className="h-4 w-4" />
      case "ai":
        return <Cpu className="h-4 w-4" />
      case "design":
        return <Globe className="h-4 w-4" />
      default:
        return <Globe className="h-4 w-4" />
    }
  }

  return (
    <div className="w-full py-16 px-4 sm:px-6 lg:px-8">
      <div className="mx-auto max-w-7xl">
        <div className="text-center mb-12">
          <h2 className="text-3xl font-bold tracking-tight sm:text-4xl md:text-5xl dark:text-white">
            Our <span className="text-primary">Featured</span> Projects
          </h2>
          <p className="mt-4 text-xl text-muted-foreground dark:text-gray-300 max-w-3xl mx-auto">
            Explore our portfolio of innovative solutions across various industries and technologies
          </p>
        </div>

        {/* Filter Buttons */}
        <div className="flex flex-wrap justify-center gap-3 mb-12">
          {filterTypes.map((type) => {
            const Icon = type.icon
            return (
              <Button
                key={type.value}
                variant={activeFilter === type.value ? "default" : "outline"}
                className="rounded-full px-6 dark:border-white border-black cursor-pointer py-2 transition-all"
                onClick={() => setActiveFilter(type.value as ProjectType)}
              >
                <Icon className="mr-2 h-4 w-4" />
                {type.label}
              </Button>
            )
          })}
        </div>

        {/* Projects Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {filteredProjects.map((project) => (
            <Card
              key={project.id}
              className="overflow-hidden max-w-[400px] shadow-2xl dark:shadow-gray-700 shadow-gray-200 group h-full flex flex-col dark:bg-gray-800 dark:border-gray-700 transition-all duration-300 hover:shadow-xl"
            >
              <div className="relative overflow-hidden aspect-video">
                <CustomImage
                  src={project.image || "/PrismX.svg"}
                  alt={project.title}
                  fallbackSrc="/PrismX.svg"
                  width={800}
                  height={600}
                  className="object-fit w-full h-full transition-transform duration-500 group-hover:scale-105"
                />
                <div className="absolute top-4 left-4 flex flex-wrap gap-2">
                  {project.type.map((type) => (
                    <Badge
                      key={type}
                      variant="secondary"
                      className="bg-black/60 backdrop-blur-sm text-white dark:bg-white/20"
                    >
                      {getProjectTypeIcon(type)}
                      <span className="ml-1">{type.charAt(0).toUpperCase() + type.slice(1)}</span>
                    </Badge>
                  ))}
                </div>
              </div>
              <CardContent className="p-6 flex-grow flex flex-col">
                <h3 className="text-xl font-bold mb-2 dark:text-white">{project.title}</h3>
                <p className="text-muted-foreground dark:text-gray-300 mb-4 flex-grow">{project.description}</p>

                <div className="flex flex-wrap gap-2 mb-4">
                  {project.technologies.map((tech) => (
                    <Badge key={tech} variant="outline" className="dark:border-gray-600 dark:text-gray-300">
                      {tech}
                    </Badge>
                  ))}
                </div>

                <div className="flex gap-3 mt-auto">
                  {project.link && (
                    <Button variant="default" size="sm" className="gap-1" asChild>
                      <Link href={project.link} target="_blank" rel="noopener noreferrer">
                        View Project <ExternalLink className="h-4 w-4 ml-1" />
                      </Link>
                    </Button>
                  )}
                  {project.github && (
                    <Button variant="outline" size="sm" className="gap-1" asChild>
                      <Link href={project.github} target="_blank" rel="noopener noreferrer">
                        <Github className="h-4 w-4 mr-1" /> Code
                      </Link>
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </div>
  )
}

